import { createContext, useState, useContext, useEffect } from "react";
import { useSymphonyApiService } from "../../../hooks/useSymphonyApi";
import { useChat } from "./ChatContext";
import { useUserProfile } from "./UserProfileContext";
import { debugLog } from "../../../configs/loggingConfig";

import { useSettings } from "./SettingsContext";

const AssistantContext = createContext();

export const CUSTOM_ASSISTANT_SENTINEL_ID = "00000000-0000-0000-0000-000000000000";

export const reconcileAssistants = (defaultAssistants = [], newAssistant) => {
    const assistantOptions = defaultAssistants ?? [];

    if (!newAssistant) return [...assistantOptions];

    if (newAssistant.assistantInstructionId === CUSTOM_ASSISTANT_SENTINEL_ID) {
        // If we have a custom assistant sentinel, remove the sentinel option and replace it with this one.
        return [
            ...assistantOptions?.filter((x) => x.assistantInstructionId !== CUSTOM_ASSISTANT_SENTINEL_ID),
            { ...newAssistant }
        ];
    }

    // If the current chat assistant isn't in the shared assistant list, append it so the select value is valid.
    const hasAssistant = assistantOptions.some(
        (x) => x.assistantInstructionId === newAssistant.assistantInstructionId
    );

    return hasAssistant ? [...assistantOptions] : [...assistantOptions, { ...newAssistant }];
}

function AssistantProvider({ children }) {

    const apiServiceClient = useSymphonyApiService("maestro", "v3");

    // Context-dependent things
    const { chat, chatId, isChatInContext, fetchChatId } = useChat();
    const { defaultChatSettings, defaultModel, defaultAssistant, userProfile } = useUserProfile();
    const { modelEncoding } = useSettings();

    // Current live assistant
    const [assistant, setAssistant] = useState(null);
    // List of available assistants
    const [assistants, setAssistants] = useState([]);
    // List of default available assistants
    const [defaultAssistants, setDefaultAssistants] = useState([]);

    // The token count for the current assistant
    const [assistantTokenCount, setAssistantTokenCount] = useState(0);

    // Updates the assistant when the chat state changes.
    useEffect(() => {
        setAssistant(chat?.assistantInstruction);
        handleAssistantChange(chat?.assistantInstruction);
    }, [chat, defaultAssistants])

    // In the event that we change the text box for the prompt, this creates a custom assistant.
    const createCustomAssistant = async (systemText) => {
        // Grab either the current assistant or the custom assistant.
        debugLog.log("[ASSISTANT] Updating to custom assistant with text", systemText);
        let newAssistant = assistant.displayName === "Custom" ? { ...assistant } : { ...assistants.find((opt) => opt.displayName === "Custom") }
        newAssistant.systemMessage = systemText;
        setAssistant(newAssistant);
    }

    // assistantTokenCount calculation
    useEffect(() => {
        let total = 0;
        const assistantEncoding = assistant?.instructionEncodings?.find(
            (x) => x.encodingEngine === modelEncoding
        );
        if (assistantEncoding) {
            total += assistantEncoding?.tokenCount;
        }
        setAssistantTokenCount(total);
    }, [assistant])

    // Gets the list of shared assistants 
    useEffect(() => {
        const getSharedAssistants = async () => {
            const customAssistant = {
                assistantInstructionId: CUSTOM_ASSISTANT_SENTINEL_ID,
                displayName: "Custom",
                systemMessage: "",
            };

            debugLog.log("[ASSISTANT] Grabbing list of assistants...");
            let respAssistants = await apiServiceClient.Assistants.getAssistants();
            if (!respAssistants) respAssistants = []
            debugLog.log("[ASSISTANT] Assistants found:", respAssistants);

            // Update the list of assistants as well as the default list, to use as a fallback.
            setAssistants([...respAssistants, { ...customAssistant }]);
            setDefaultAssistants([...respAssistants, { ...customAssistant }]);
        }

        getSharedAssistants();
    }, [userProfile])

    // This handles the assistants state and fills it in with all the appropriate options. 
    const handleAssistantChange = (newAssistant) => {
        if (!isChatInContext()) return;

        if (!newAssistant) return;

        setAssistants(reconcileAssistants(defaultAssistants, newAssistant));
    }

    // This saves any assistant change to the API.
    const updateAssistant = async (newAssistant) => {
        if (!newAssistant) return;
        // If we're working with a custom assistant, we ALWAYS want to update. Otherwise, don't update if we don't have to.
        if (newAssistant.displayName !== "Custom" && newAssistant.assistantInstructionId === assistant?.assistantInstructionId) return;

        setAssistant(newAssistant);

        if (isChatInContext()) {
            debugLog.log("[ASSISTANT] Saving assistant to current chat:", newAssistant);
            await apiServiceClient.Chats.updateAssistantInstruction(chatId, newAssistant.assistantInstructionId, newAssistant.systemMessage)
        }
        else {
            if (newAssistant.displayName === "Custom") {
                if (newAssistant.systemMessage === "") return;
                const chatId = await fetchChatId({ "assistantInstruction": newAssistant });
                //const assistant = await apiServiceClient.Chats.updateAssistantInstruction(chatId, newAssistant.assistantInstructionId, newAssistant.systemMessage);
                setAssistant(newAssistant);
            } else {
                debugLog.log("[ASSISTANT] Saving assistant to user profile:", newAssistant);
                await apiServiceClient.UserProfiles.updateUserAssistantInstruction(newAssistant.assistantInstructionId, newAssistant.systemMessage)
            }
        }
    }

    // If we leave a chat, reset assistant
    useEffect(() => {
        if (isChatInContext()) return;

        setAssistant(defaultAssistant);
        setAssistants(defaultAssistants);
    }, [chatId, defaultAssistant, isChatInContext])


    return (
        <AssistantContext.Provider
            value={{
                // States
                assistant,
                setAssistant,
                assistants,
                setAssistants,
                assistantTokenCount,
                defaultAssistant,
                // Functions
                createCustomAssistant,
                updateAssistant,
            }}
        >
            {children}
        </AssistantContext.Provider>
    );
}

// Hook to use the AssistantContext in a component
function useAssistant() {
    const context = useContext(AssistantContext);
    if (context === undefined) {
        throw new Error(
            "useAssistant must be used within a AssistantProvider"
        );
    }
    return context;
}

export { AssistantProvider, useAssistant };
